import React, { useEffect, useMemo, useState } from "react";
import { Redirect, useHistory, useLocation } from "react-router-dom";
import Main from "../../components/layout/Main";
import Sheet from "../../components/layout/app/Sheet";
import AppModal from "../../components/layout/app/AppModal";
import { useToggleTop } from "../../components/common/ToggleTop";
import { apiMessage } from "../../api/apiMessage";
import { UserService } from "../../services/UserService";
import { User } from "../../storage/store";
import {
  DEVICE_TYPES as T,
  EDITABLE_TYPES,
  FIXED_TYPES,
  GATE_CONTROLLER_MODELS,
  modelOf,
  typeOf,
} from "../../constants/devices";
import "../../assets/styles/app-device.css";

const GATE_SWITCH = "GATE SWITCH";

// The app's Edit Device page (app/screens/EditSwitchCommon.js): rename a
// device, move it to another room, change its type or power, or remove it.
function EditDevice() {
  const location = useLocation();
  const navigate = useHistory();
  const toast = useToggleTop();
  const passed = location.state?.data;
  const [config, setConfig] = useState(null);
  const [form, setForm] = useState(null);
  const [picker, setPicker] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [confirmDelete, setConfirmDelete] = useState(false);

  useEffect(() => {
    if (!passed) return;
    UserService.getConfig(User.getPropertyId())
      .then((result) => {
        setConfig(result);
        const saved = result?.devices?.find((item) => String(item.applianceId) === String(passed.applianceId)) ?? passed;
        setForm({
          name: saved.name ?? "",
          floorId: saved.floorId,
          floor: saved.floor,
          roomId: saved.roomId,
          room: saved.room,
          deviceType: saved.deviceType,
          power: saved.meta?.power ?? "",
        });
      })
      .catch(() => toast("Couldn't load this device. Please try again.", "red"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [passed]);

  // The device as saved in the config, without the live state the pages add.
  const saved = useMemo(
    () => config?.devices?.find((item) => String(item.applianceId) === String(passed?.applianceId)) ?? null,
    [config, passed]
  );

  if (!passed) return <Redirect to="/dashboard" />;

  const type = typeOf(passed);
  const gateModel = GATE_CONTROLLER_MODELS.includes(modelOf(passed));
  const typeLocked = FIXED_TYPES.includes(type);
  const typeOptions = gateModel ? [GATE_SWITCH] : EDITABLE_TYPES;
  const floors = config?.floors ?? [];
  const rooms = floors.find((floor) => floor._id === form?.floorId)?.rooms ?? [];

  const changed =
    form &&
    saved &&
    (form.name !== saved.name ||
      form.roomId !== saved.roomId ||
      form.floorId !== saved.floorId ||
      form.deviceType !== saved.deviceType ||
      String(form.power) !== String(saved.meta?.power ?? ""));

  const update = async () => {
    if (!form.name.trim()) {
      setError("Name is required");
      return;
    }
    setError("");
    setSaving(true);
    try {
      await UserService.updateDevice(
        {
          ...saved,
          name: form.name.trim(),
          deviceType: form.deviceType,
          floor: form.floor,
          floorId: form.floorId,
          room: form.room,
          roomId: form.roomId,
          meta: { ...saved.meta, power: form.power === "" ? saved.meta?.power : Number(form.power) },
        },
        User.getPropertyId()
      );
      toast("Device updated successfully!", "green");
      navigate.goBack();
    } catch (err) {
      toast(apiMessage(err, "Couldn't update the device. Please try again."), "red");
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    setConfirmDelete(false);
    try {
      await UserService.deleteDevice(User.getPropertyId(), passed.deviceId);
      toast("Device removed successfully!", "green");
      navigate.push("/dashboard");
    } catch (err) {
      toast(apiMessage(err, "Couldn't remove the device. Please try again."), "red");
    }
  };

  const chooseFloor = (floor) => {
    setForm({ ...form, floorId: floor._id, floor: floor.name, roomId: null, room: "" });
    // As in the app, picking a floor goes straight on to its rooms.
    setPicker("room");
  };

  const chooseType = (option) => {
    setForm({ ...form, deviceType: option === GATE_SWITCH ? T.SLIDING_GATE : option });
    setPicker(null);
  };

  return (
    <Main
      header="stack"
      title="Edit Device"
      hideTabBar
      headerRight={
        <button type="button" className="hx-edit-update" disabled={!changed || saving} onClick={update}>
          {saving ? "Saving…" : "Update"}
        </button>
      }
    >
      {!form ? (
        <div className="hx-edit" style={{ display: "grid", gap: 18, paddingTop: 20 }}>
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className="hx-skel" style={{ minHeight: 56 }} />
          ))}
        </div>
      ) : (
        <form
          className="hx-edit"
          onSubmit={(event) => {
            event.preventDefault();
            if (changed) update();
          }}
        >
          <label className="hx-field">
            <span className="hx-field-label">Name</span>
            <input
              className="hx-field-input"
              value={form.name}
              onChange={(event) => setForm({ ...form, name: event.target.value })}
              autoComplete="off"
              aria-invalid={Boolean(error)}
            />
            {error ? (
              <p className="hx-field-error" role="alert">
                {error}
              </p>
            ) : null}
          </label>

          <div className="hx-field">
            <span className="hx-field-label" id="hx-room-label">
              Room
            </span>
            <button type="button" className="hx-field-input" aria-labelledby="hx-room-label" onClick={() => setPicker("room")}>
              {form.room || "Choose a room"}
            </button>
          </div>

          <div className="hx-field">
            <span className="hx-field-label" id="hx-floor-label">
              Floor
            </span>
            <button type="button" className="hx-field-input" aria-labelledby="hx-floor-label" onClick={() => setPicker("floor")}>
              {form.floor || "Choose a floor"}
            </button>
          </div>

          <div className="hx-field">
            <span className="hx-field-label" id="hx-type-label">
              Device Type
            </span>
            <button
              type="button"
              className="hx-field-input"
              aria-labelledby="hx-type-label"
              disabled={typeLocked}
              onClick={() => setPicker("type")}
            >
              {form.deviceType}
            </button>
          </div>

          {type !== T.ENERGY_METER ? (
            <label className="hx-field">
              <span className="hx-field-label">Device Power in Watts</span>
              <input
                className="hx-field-input"
                type="number"
                min="0"
                inputMode="numeric"
                placeholder="Power"
                value={form.power}
                onChange={(event) => setForm({ ...form, power: event.target.value })}
              />
            </label>
          ) : null}

          <button type="button" className="hx-delete-link" onClick={() => setConfirmDelete(true)}>
            DELETE SWITCH
          </button>

          <p className="hx-info-foot">
            <b>Manufacturer:</b>
            <span>HASHh Automations. Connected through HASHh Cloud.</span>
          </p>
        </form>
      )}

      <Sheet open={picker === "floor"} onClose={() => setPicker(null)} title="Choose your Floor">
        <div className="hx-choice-list">
          {floors.map((floor) => (
            <button
              key={floor._id}
              type="button"
              className={`hx-choice ${floor._id === form?.floorId ? "is-selected" : ""}`}
              onClick={() => chooseFloor(floor)}
            >
              {floor.name}
            </button>
          ))}
        </div>
      </Sheet>

      <Sheet open={picker === "room"} onClose={() => setPicker(null)} title="Choose your Room">
        <div className="hx-choice-list">
          {rooms.length ? (
            rooms.map((room) => (
              <button
                key={room._id}
                type="button"
                className={`hx-choice ${room._id === form?.roomId ? "is-selected" : ""}`}
                onClick={() => {
                  setForm({ ...form, roomId: room._id, room: room.name });
                  setPicker(null);
                }}
              >
                {room.name}
              </button>
            ))
          ) : (
            <p className="hx-modal-text">This floor has no rooms yet.</p>
          )}
        </div>
      </Sheet>

      <Sheet open={picker === "type"} onClose={() => setPicker(null)} title="Device Type">
        <div className="hx-choice-list">
          {typeOptions.map((option) => (
            <button
              key={option}
              type="button"
              className={`hx-choice ${(option === GATE_SWITCH ? T.SLIDING_GATE : option) === form?.deviceType ? "is-selected" : ""}`}
              onClick={() => chooseType(option)}
            >
              {option}
            </button>
          ))}
        </div>
      </Sheet>

      <AppModal
        open={confirmDelete}
        onClose={() => setConfirmDelete(false)}
        title="Delete Switch"
        actions={
          <>
            <button type="button" className="hx-btn is-ghost" onClick={() => setConfirmDelete(false)}>
              Cancel
            </button>
            <button type="button" className="hx-btn" onClick={remove}>
              Yes
            </button>
          </>
        }
      >
        <p className="hx-modal-text">Are you sure you want to delete this Switch?</p>
      </AppModal>
    </Main>
  );
}

export default EditDevice;
